import { useEffect, useMemo, useState } from "react";

import { cloneDeep } from "lodash";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";

import { useWizard, Wizard } from "@components/wizard";
import { MMMState } from "@north-beam/nb-client-side";
import { type GetBasicMediaPlans_me_mmm_basicMediaPlansData_plans } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { CreateMediaPlanTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { useMediaPlans } from "../hooks/use-media-plans";

import { BasicMediaPlanModalTabs } from "./basic-media-plan-modal-tabs";
import { WIZARD_STEPS_AUTOBLESSED_MODELS } from "./consts";
import { BasicInformationStep, ReviewAndCreateStep } from "./steps";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedMediaPlanId?: string | null;
}

export type MediaPlan = {
  id?: string | null;
  name: string;
  start_date: string | null;
  end_date: string | null;
  model_configuration_id: string;
};

type VerifiedCreateMediaPlanData = {
  name: string;
  start_date: string;
  end_date: string;
  model_configuration_id: string;
};

type VerifiedUpdateMediaPlanData = VerifiedCreateMediaPlanData & {
  id: string;
};

export const CreateBasicMediaPlanModal = ({
  isOpen,
  onClose,
  selectedMediaPlanId,
}: Props) => {
  const {
    availableConfigs,
    createMediaPlan,
    loadingCreateMediaPlan,
    getMediaPlanById,
    updateMediaPlan,
    loadingUpdateMediaPlan,
    finalizeMediaPlan,
    isFinalizingMediaPlanLoading,
  } = useMediaPlans();

  const trackMMMEvent = useTrackMMMEvent(MMMTab.CREATE_MEDIA_PLAN);

  const selectedMediaPlanFromId =
    useMemo<GetBasicMediaPlans_me_mmm_basicMediaPlansData_plans | null>(() => {
      if (!selectedMediaPlanId) {
        return null;
      }
      return getMediaPlanById(selectedMediaPlanId);
    }, [selectedMediaPlanId, getMediaPlanById]);

  const [newMediaPlan, setNewMediaPlan] =
    useState<GetBasicMediaPlans_me_mmm_basicMediaPlansData_plans | null>(
      selectedMediaPlanFromId,
    );

  const selectedMediaPlan = useMemo(() => {
    return selectedMediaPlanFromId || newMediaPlan;
  }, [selectedMediaPlanFromId, newMediaPlan]);

  const isDraft = selectedMediaPlan?.state === MMMState.draft;
  const hasNoBlessedModel =
    isDraft && selectedMediaPlan && !selectedMediaPlan.blessed_model_id;

  const defaultFormValues = useMemo(() => {
    if (!selectedMediaPlan) {
      return {
        id: null,
        name: "",
        start_date: null,
        end_date: null,
        model_configuration_id:
          availableConfigs.length > 0 ? availableConfigs[0].config_id : "",
      };
    }

    return {
      id: selectedMediaPlan.id,
      name: selectedMediaPlan.name,
      start_date: selectedMediaPlan.date_range.start_date,
      end_date: selectedMediaPlan.date_range.end_date,
      model_configuration_id: selectedMediaPlan.model_config?.config_id ?? "",
    };
  }, [selectedMediaPlan, availableConfigs]);

  const { addToast } = useToasts();

  const form = useForm<MediaPlan>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    form.reset(defaultFormValues);
  }, [defaultFormValues, form]);

  const name = form.watch("name");
  const model_configuration_id = form.watch("model_configuration_id");
  const start_date = form.watch("start_date");
  const end_date = form.watch("end_date");

  const isBasicInfoFormValid = useMemo(
    () => name && model_configuration_id && start_date && end_date,
    [name, model_configuration_id, start_date, end_date],
  );
  const WIZARD_STEPS = WIZARD_STEPS_AUTOBLESSED_MODELS;

  const { currentIndex, goToNext, goToPrevious, setStep } = useWizard();

  const _onClose = () => {
    setNewMediaPlan(null);
    setStep(0);
    form.reset();
    onClose();
  };

  const onSubmit = async () => {
    trackMMMEvent({
      component: CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
      action: "onSubmit",
      actionInfo: "Attempt to finalize media plan",
    });
    if (
      !selectedMediaPlan ||
      (!selectedMediaPlan.blessed_model_id &&
        !selectedMediaPlan.connected_model_id) ||
      !selectedMediaPlan.id
    ) {
      // If there is no media plan or neither blessed_model_id or connected_model_id or no set id
      addToast(
        <div>
          Your plan does not have all the required information. Please fill out
          the form and try again.
        </div>,
        { appearance: "error" },
      );
      return;
    }
    try {
      // Since blessing a model and connecting a model are two different endpoints
      // and don't occur in a DB transaction, we can handle the instance where
      // the model is connected but the cycle isn't transferred out of draft by
      // using a connected_model_id if it exists and no blessed_model_id does to
      // allow the media plan to be finalized
      await finalizeMediaPlan({
        id: selectedMediaPlan.id,
        modelId: (selectedMediaPlan.blessed_model_id ??
          selectedMediaPlan.connected_model_id) as string,
      });
      trackMMMEvent({
        component: CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
        action: "onSubmit",
        actionInfo: "Successfully finalize media plan",
      });
      _onClose();
    } catch (err) {
      trackMMMEvent({
        component: CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
        action: "onSubmit",
        actionInfo: "Failed to finalize media plan",
      });
      addToast(
        <div>
          There was an error finalizing the media plan. Please try again.
        </div>,
        { appearance: "error" },
      );
    }
  };

  const wizardSteps = useMemo(() => {
    return [
      {
        children: (
          <BasicInformationStep
            form={form}
            currentIndex={currentIndex}
            modelConfigurations={availableConfigs}
            model_configuration_id={model_configuration_id}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
      {
        children: (
          <ReviewAndCreateStep
            currentIndex={currentIndex}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
    ];
  }, [
    currentIndex,
    selectedMediaPlan,
    availableConfigs,
    form,
    model_configuration_id,
  ]);

  const goToNextInWizard = async () => {
    if (currentIndex === 0) {
      const selectedModelConfig = availableConfigs.find(
        (config) => config.config_id === model_configuration_id,
      );
      const mediaPlanData = cloneDeep(form.getValues());

      if (
        !selectedModelConfig ||
        !mediaPlanData.start_date ||
        !mediaPlanData.end_date ||
        !mediaPlanData.name
      ) {
        addToast("Please fill out all required fields", {
          appearance: "error",
        });
        return;
      }

      if (
        !selectedMediaPlan &&
        selectedModelConfig &&
        model_configuration_id &&
        mediaPlanData.start_date &&
        mediaPlanData.end_date &&
        mediaPlanData.name
      ) {
        // No cycle has yet been saved, is just in the form,
        // and all required fields have data
        const createdMediaPlan = await createMediaPlan(
          mediaPlanData as VerifiedCreateMediaPlanData,
        );
        if (createdMediaPlan) {
          setNewMediaPlan(createdMediaPlan);
          trackMMMEvent({
            component:
              CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL_NEXT_BUTTON,
            action: "onClick",
            actionInfo: "Successfully created new draft media plan",
          });
        } else {
          trackMMMEvent({
            component:
              CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL_NEXT_BUTTON,
            action: "onClick",
            actionInfo: "Create draft media plan failed",
          });
          addToast("Failed to create new media plan", {
            appearance: "error",
          });
        }
      } else if (
        selectedMediaPlan &&
        selectedModelConfig &&
        mediaPlanData.id &&
        (selectedMediaPlan.name !== name ||
          selectedMediaPlan.date_range.start_date !== start_date ||
          selectedMediaPlan.date_range.end_date !== end_date ||
          selectedMediaPlan.model_config?.config_id !== model_configuration_id)
      ) {
        // The cycle has already been saved but some data has changed
        const updatedMediaPlan = await updateMediaPlan(
          mediaPlanData as VerifiedUpdateMediaPlanData,
        );
        if (updatedMediaPlan) {
          setNewMediaPlan(updatedMediaPlan);
          trackMMMEvent({
            component:
              CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL_NEXT_BUTTON,
            action: "onClick",
            actionInfo: "Successfully updated draft media plan",
          });
        } else {
          trackMMMEvent({
            component:
              CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL_NEXT_BUTTON,
            action: "onClick",
            actionInfo: "Update draft media plan failed",
          });
          addToast("Failed to update media plan", {
            appearance: "error",
          });
        }
      }
      goToNext();
    } else {
      // The cycle has already been saved and no changes have been made
      goToNext();
    }
  };

  const isLoading =
    loadingCreateMediaPlan ||
    loadingUpdateMediaPlan ||
    isFinalizingMediaPlanLoading;

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={_onClose}
      hideCloseButton
      bodyClassName="px-0 py-0"
    >
      <div className="p-1.5 bg-gray-3 flex gap-1.5 justify-between h-[600px] relative">
        <div className="absolute top-10 right-8" onClick={_onClose}>
          <i className="fa-regular fa-close text-cosmic cursor-pointer" />
        </div>
        <BasicMediaPlanModalTabs
          className="w-1/3 max-w-[286px] bg-v2-primary rounded border border-v2-secondary"
          currentIndex={currentIndex}
          setStep={setStep}
          selectedMediaPlan={selectedMediaPlan}
        />
        <div className="flex-grow bg-v2-primary rounded border border-v2-secondary flex flex-col justify-between">
          <Wizard
            className="p-6 pb-0 overflow-y-scroll"
            currentIndex={currentIndex}
            goToNext={goToNext}
            goToPrevious={goToPrevious}
            setStep={setStep}
            submitText="Create Media Plan"
            onSubmit={form.handleSubmit(onSubmit)}
            shouldHideDefaultFooter
            steps={wizardSteps}
          />
          <div className="px-6 bg-[#FAFAFA] h-16 flex items-center justify-between space-x-2 min-h-[4rem]">
            <TertiaryButton onClick={_onClose}>Cancel</TertiaryButton>
            {currentIndex < WIZARD_STEPS.length - 1 && (
              <PrimaryButton
                onClick={goToNextInWizard}
                loading={isLoading}
                disabled={
                  (currentIndex === 0 && !isBasicInfoFormValid) || isLoading
                }
              >
                Next
              </PrimaryButton>
            )}
            {currentIndex === WIZARD_STEPS.length - 1 && isDraft && (
              <PrimaryButton
                loading={isLoading}
                disabled={
                  hasNoBlessedModel && !selectedMediaPlan.connected_model_id
                }
                onClick={form.handleSubmit(onSubmit)}
              >
                CREATE NEW PLAN
              </PrimaryButton>
            )}
            {currentIndex === WIZARD_STEPS.length - 1 && !isDraft && (
              <PrimaryButton onClick={_onClose}>CLOSE</PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
